/**
 * Achilles — „Battle of Legends, Volume Two".
 *
 * Celá postava stojí na Patroklovi: kým žije, Achilles je opatrnejší (karty
 * dávajú akcie a výmeny miest), keď padne, Achilles zúri — +2 ku každému
 * útoku a karta za každý vyhraný súboj, ale hneď pri páde odhodí 2 náhodné.
 *
 * Test for Weakness nesie **bonusový útok** (Relentless Assault): po súboji
 * nasleduje ďalší útok toho istého útočníka na toho istého obrancu, bez akcie.
 * (Battle of Legends Vol. 2, „Bonus attacks")
 */

import { shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import {
  askChoice,
  dealDamage,
  discardRandom,
  drawCard,
  emit,
  gainAction,
  getMap,
  heroOf,
  swapFighters,
} from '../ops.js';
import type { FighterId, PlayerIndex } from '../types.js';
import { feint, skirmish } from './common.js';
import { adjacentFighters, askMove, enemyFighters, fighterOrNull } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const PATROCLUS = 'patroclus';

/** Patroklos hráča, ak ešte žije. */
export function patroclusOf(ctx: Ctx, player: PlayerIndex): FighterId | null {
  return ctx.state.fighters.find((f) => f.owner === player && f.defId === PATROCLUS)?.id ?? null;
}

function isAchilles(ctx: Ctx, fighterId: FighterId | null): boolean {
  return fighterOrNull(ctx, fighterId)?.defId === 'achilles';
}

/**
 * Bonusový útok. Nespustí sa hneď — zaradí sa do fronty, aby sa najprv
 * dohrali efekty obrancu (Skirmish ho môže odsunúť z dosahu; bonusový útok
 * ho zasiahne aj tak). Ak obranca padol už v prvom útoku, bonus nie je.
 */
function bonusAttack(name: string, value: number): CardEffect {
  return {
    after: (e) => {
      if (!e.fighterId || !e.opponentFighterId) return;
      askChoice(e.ctx, {
        kind: 'bonusAttack',
        player: e.player,
        source: e.cardId,
        label: `${name}: bonusový útok za ${value}`,
        optional: false,
        attackerFighter: e.fighterId,
        defenderFighter: e.opponentFighterId,
        name,
        value,
      });
    },
  };
}

/** „Deal 2 damage to both fighters in the combat." */
const battleFrenzy: CardEffect = {
  after: (e) => {
    for (const id of [e.fighterId, e.opponentFighterId]) if (id) dealDamage(e.ctx, id, 2);
  },
};

/**
 * „DURING COMBAT: You may deal 2 damage to Patroclus. If you do, the value
 * of this attack is 5 instead." Rozhodnutie uprostred súboja — súboj počká.
 */
const theDayOfYourDoom: CardEffect = {
  duringChoice: (e) => {
    if (!patroclusOf(e.ctx, e.player)) return;
    askChoice(e.ctx, {
      kind: 'confirm',
      player: e.player,
      source: e.cardId,
      label: 'The Day of Your Doom: dať Patroklovi 2 poškodenia? Útok potom platí 5.',
      optional: true,
      effect: 'achilles.the-day-of-your-doom',
      yes: 'Áno, útok za 5',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, choice) => {
    const patroclus = patroclusOf(ctx, choice.player);
    if (!patroclus) return;
    dealDamage(ctx, patroclus, 2);
    ctx.state.combat?.flags?.push('doom');
  },
  during: (e, value) => (e.ctx.state.combat?.flags?.includes('doom') ? 5 : value),
};

/** „If Patroclus is not defeated, gain 1 action." */
const brothersInArms: CardEffect = {
  after: (e) => {
    if (patroclusOf(e.ctx, e.player)) gainAction(e.ctx, e.player);
  },
};

/** „Choose a card in your discard pile and return it to your hand." */
const battleHardened: CardEffect = {
  after: (e) => {
    const discard = e.ctx.state.players[e.player]?.discard ?? [];
    if (discard.length === 0) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Vyber kartu z odhodu, ktorá sa vráti do ruky',
      optional: false,
      from: 'discard',
      cards: discard.slice(),
      purpose: 'toHand',
    });
  },
};

/** „Deal 1 damage to each opposing fighter adjacent to your fighter." */
const wilyFighting: CardEffect = {
  after: (e) => {
    const fighter = fighterOrNull(e.ctx, e.fighterId);
    if (!fighter) return;
    const enemies = new Set(enemyFighters(e.ctx, e.player));
    for (const id of adjacentFighters(e.ctx, fighter.spaceId)) {
      if (enemies.has(id)) dealDamage(e.ctx, id, 1);
    }
  },
};

/** „Move each of your fighters up to 3 spaces. They may move through opposing fighters." */
const blessedByHermes: CardEffect = {
  after: (e) => {
    const mine = e.ctx.state.fighters.filter((f) => f.owner === e.player);
    for (const fighter of mine) {
      askMove(e, {
        fighters: [fighter.id],
        max: 3,
        through: true,
        label: `Presuň ${fighter.name} až o 3 priestory (smie prejsť cez súperove figúrky)`,
      });
    }
  },
};

/**
 * „IMMEDIATELY: If Patroclus is not defeated, Achilles may swap spaces with
 * him. If he does, Patroclus is now the defender."
 */
const underAchillesHelm: CardEffect = {
  immediate: (e) => {
    if (!e.fighterId || !patroclusOf(e.ctx, e.player)) return;
    askChoice(e.ctx, {
      kind: 'confirm',
      player: e.player,
      source: e.cardId,
      label: "Under Achilles' Helm: vymeniť Achilla s Patroklom? Obrancom bude Patroklos.",
      optional: true,
      effect: 'achilles.under-achilles-helm',
      yes: 'Vymeniť',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, choice) => {
    const combat = ctx.state.combat;
    const achilles = heroOf(ctx.state, choice.player);
    const patroclus = patroclusOf(ctx, choice.player);
    if (!combat || !achilles || !patroclus) return;
    swapFighters(ctx, achilles.id, patroclus);
    combat.defenderFighter = patroclus;
    emit(ctx, { type: 'defenderChanged', fighterId: patroclus });
  },
};

/** „If you lost the combat, your opponent gains 1 action." */
const achillesHeel: CardEffect = {
  after: (e) => {
    if (!e.won && e.opponentPlayer !== null) gainAction(e.ctx, e.opponentPlayer);
  },
};

/** „Deal 2 damage to an opposing fighter in Achilles' zone." */
const spearThrow: CardEffect = {
  scheme: (e: EffectCtx) => {
    const achilles = heroOf(e.ctx.state, e.player);
    if (!achilles) return;
    const map = getMap(e.ctx);
    const targets = enemyFighters(e.ctx, e.player).filter((id) => {
      const target = fighterOrNull(e.ctx, id);
      return target ? shareZone(map, achilles.spaceId, target.spaceId) : false;
    });
    askChoice(e.ctx, {
      kind: 'damage',
      player: e.player,
      source: e.cardId,
      label: 'Spear Throw: 2 poškodenia súperovej figúrke v Achillovej zóne',
      optional: false,
      amount: 2,
      targets,
    });
  },
};

export const achillesCards: EffectRegistry = {
  'achilles.battle-frenzy': battleFrenzy,
  'achilles.test-for-weakness': bonusAttack('Relentless Assault', 3),
  'achilles.the-day-of-your-doom': theDayOfYourDoom,
  'achilles.brothers-in-arms': brothersInArms,
  'achilles.feint': feint,
  'achilles.battle-hardened': battleHardened,
  'achilles.wily-fighting': wilyFighting,
  'achilles.blessed-by-hermes': blessedByHermes,
  'achilles.skirmish': skirmish(2),
  'achilles.under-achilles-helm': underAchillesHelm,
  'achilles.achilles-heel': achillesHeel,
  'achilles.spear-throw': spearThrow,
};

/**
 * „When Patroclus is defeated, discard 2 random cards. While Patroclus is
 * defeated: +2 to the value of all Achilles' attacks; if Achilles wins
 * combat, draw 1 card." Karta sa ťahá až po efektoch AFTER COMBAT (poznámka
 * k balíčku v zdroji).
 */
export const achillesSpecial: HeroSpecial = {
  name: 'Hnev za Patrokla',
  onDefeated: (ctx, player, fighter) => {
    if (fighter.defId !== PATROCLUS) return;
    emit(ctx, { type: 'specialUsed', player, heroId: 'achilles', name: 'Patroklos padol' });
    discardRandom(ctx, player);
    discardRandom(ctx, player);
  },
  attackModifier: (ctx, player, fighterId) =>
    isAchilles(ctx, fighterId) && !patroclusOf(ctx, player) ? 2 : 0,
  afterCombat: (ctx, player, fighterId, won) => {
    if (won && isAchilles(ctx, fighterId) && !patroclusOf(ctx, player)) drawCard(ctx, player);
  },
};
